import { useState } from 'react';
import styled from 'styled-components';
import { usePreviewGeoSchemeMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  ErrorText,
  Hint,
  Muted,
  Panel,
  PanelHead,
  PanelTitle,
  Stack,
  useAction,
  useObjectUrl,
} from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

const Frame = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};

  img {
    display: block;
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    box-shadow: ${({ theme }) => theme.shadow.md};
  }
`;

/**
 * «Показать черновик»: PNG листа по СОХРАНЁННЫМ настройкам, версия не создаётся.
 * Несохранённые правки шага сначала сохраняются (`beforePreview`).
 */
export function DraftPreview({
  beforePreview,
  blockedReason,
}: {
  beforePreview?: () => Promise<boolean>;
  blockedReason?: string | null;
}) {
  const { scheme, readOnly } = useWizard();
  const [preview] = usePreviewGeoSchemeMutation();
  const { runValue, busy, error } = useAction();
  const [blob, setBlob] = useState<Blob | null>(null);
  const url = useObjectUrl(blob);

  if (readOnly) return null;

  async function show() {
    if (beforePreview) {
      const ok = await beforePreview();
      if (!ok) return;
    }
    const png = await runValue(() => preview(scheme.id).unwrap(), 'Черновик не построен.');
    if (png) setBlob(png);
  }

  return (
    <Panel>
      <PanelHead>
        <PanelTitle>Черновик листа</PanelTitle>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={() => void show()}
          disabled={busy || Boolean(blockedReason)}
        >
          {busy ? 'Строим…' : blob ? 'Обновить черновик' : 'Показать черновик'}
        </Button>
      </PanelHead>
      <Stack $gap={3}>
        <Hint>
          Рамка, штамп формы 3, области и тексты — как лягут на лист. Строится по сохранённым
          настройкам, версия схемы при этом не создаётся.
        </Hint>
        {blockedReason && <Muted>{blockedReason}</Muted>}
        {error && <ErrorText role="alert">{error}</ErrorText>}
        {url && (
          <Frame>
            <img src={url} alt={`Черновик листа схемы ${scheme.number}`} />
          </Frame>
        )}
      </Stack>
    </Panel>
  );
}
